import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const COLORS = {
  sensory: '#2493A2',
  integration: '#57BDA2',
  symbolic: '#DCB13C',
  capstone: '#E85D75',
};

interface EventDot {
  id: number;
  label: string;
  scatterX: number;
  scatterY: number;
  arcX: number;
  arcY: number;
}

export function StoryArcBuilder() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    if (!svgRef.current) return;

    svg.selectAll('*').remove();

    const W = 320;
    const pad = 30;

    const eventLabels = ['event A', 'event B', 'event C', 'event D', 'event E', 'event F'];

    // Story arc: a classic curve (rise -> climax -> fall)
    // x positions evenly spread, y follows an arc
    const arcXPositions = eventLabels.map((_, i) => pad + 20 + i * ((W - 2 * pad - 40) / (eventLabels.length - 1)));

    // Arc y-values: beginning low, climax high (low y), resolution mid
    const arcYValues = [120, 80, 42, 38, 70, 115];

    // Scattered positions (random-looking but deterministic)
    const scatterPositions = [
      { x: 240, y: 45 },
      { x: 60, y: 110 },
      { x: 180, y: 30 },
      { x: 100, y: 80 },
      { x: 280, y: 100 },
      { x: 35, y: 50 },
    ];

    const events: EventDot[] = eventLabels.map((label, i) => ({
      id: i,
      label,
      scatterX: scatterPositions[i].x,
      scatterY: scatterPositions[i].y,
      arcX: arcXPositions[i],
      arcY: arcYValues[i],
    }));

    // -- Arc path (hidden initially) --
    const lineGen = d3.line<EventDot>()
      .x(d => d.arcX)
      .y(d => d.arcY)
      .curve(d3.curveCatmullRom.alpha(0.5));

    const arcPath = svg.append('path')
      .datum(events)
      .attr('d', lineGen)
      .attr('fill', 'none')
      .attr('stroke', COLORS.symbolic)
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', function() {
        const len = (this as SVGPathElement).getTotalLength();
        return `${len} ${len}`;
      })
      .attr('stroke-dashoffset', function() {
        return (this as SVGPathElement).getTotalLength();
      })
      .attr('opacity', 0);

    // Arc section labels
    const sectionLabels = [
      { text: 'beginning', x: arcXPositions[0] + 10, y: 140 },
      { text: 'climax', x: (arcXPositions[2] + arcXPositions[3]) / 2, y: 26 },
      { text: 'end', x: arcXPositions[5] - 5, y: 140 },
    ];

    const sectionTexts = svg.append('g').attr('opacity', 0);
    sectionLabels.forEach(sl => {
      sectionTexts.append('text')
        .attr('x', sl.x)
        .attr('y', sl.y)
        .attr('text-anchor', 'middle')
        .attr('fill', 'var(--color-text-muted)')
        .attr('font-size', '8px')
        .attr('font-style', 'italic')
        .text(sl.text);
    });

    // -- Dots and labels --
    const dotGroup = svg.append('g');

    const dotSels = events.map(ev => {
      const g = dotGroup.append('g')
        .attr('transform', `translate(${ev.scatterX}, ${ev.scatterY})`);

      g.append('circle')
        .attr('r', 6)
        .attr('fill', COLORS.sensory)
        .attr('opacity', 0.85);

      g.append('text')
        .attr('dy', -10)
        .attr('text-anchor', 'middle')
        .attr('fill', 'var(--color-text)')
        .attr('font-size', '8px')
        .text(ev.label);

      return { g, ev };
    });

    // -- Animation cycle --
    let cancelled = false;

    function runCycle() {
      if (cancelled) return;

      // Reset to scattered
      dotSels.forEach(({ g, ev }) => {
        g.attr('transform', `translate(${ev.scatterX}, ${ev.scatterY})`);
        g.select('circle').attr('fill', COLORS.sensory);
      });
      arcPath.attr('opacity', 0);
      sectionTexts.attr('opacity', 0);

      // Phase 2: reorder onto arc after delay
      dotSels.forEach(({ g, ev }, i) => {
        g.transition()
          .delay(1500 + i * 150)
          .duration(600)
          .ease(d3.easeCubicInOut)
          .attr('transform', `translate(${ev.arcX}, ${ev.arcY})`)
          .select('circle')
          .attr('fill', COLORS.symbolic);
      });

      // Draw the arc line
      arcPath
        .attr('opacity', 0)
        .transition()
        .delay(2200)
        .duration(200)
        .attr('opacity', 0.7)
        .transition()
        .duration(800)
        .ease(d3.easeLinear)
        .attr('stroke-dashoffset', 0);

      // Show section labels
      sectionTexts.transition()
        .delay(2800)
        .duration(400)
        .attr('opacity', 0.8);

      // Hold then reset
      setTimeout(() => {
        if (cancelled) return;

        // Fade out arc and labels
        arcPath.transition().duration(400).attr('opacity', 0)
          .on('end', () => {
            // Reset dashoffset for next cycle
            arcPath.attr('stroke-dashoffset', function() {
              return (this as SVGPathElement).getTotalLength();
            });
          });
        sectionTexts.transition().duration(400).attr('opacity', 0);

        // Move dots back to scatter
        dotSels.forEach(({ g, ev }, i) => {
          g.transition()
            .delay(200 + i * 80)
            .duration(500)
            .ease(d3.easeCubicInOut)
            .attr('transform', `translate(${ev.scatterX}, ${ev.scatterY})`);
          g.select('circle').transition()
            .delay(200 + i * 80)
            .duration(500)
            .attr('fill', COLORS.sensory);
        });

        setTimeout(() => {
          if (!cancelled) runCycle();
        }, 1800);
      }, 5500);
    }

    const startTimeout = setTimeout(runCycle, 200);

    return () => {
      cancelled = true;
      clearTimeout(startTimeout);
      svg.selectAll('*').interrupt();
      svg.selectAll('*').remove();
    };
  }, []);

  return (
    <div className="viz-container">
      <h4 className="viz-container__title">
        <i className="fa-solid fa-book-open" /> Story Arc Builder
      </h4>
      <svg ref={svgRef} viewBox="0 0 320 160" />
      <p className="viz-container__caption">Retelling edits reality.</p>
    </div>
  );
}
